import axios from 'axios'
import config from '../../variables/config'

/**
 * Canonical price keys used by the portal. Replace or override via
 * GET /Memberships/membershipPricing (same shape, partial responses are merged).
 */
export const DEFAULT_MEMBERSHIP_PRICES = {
  standard: { monthly: 11.5, yearlyMonthEquiv: 9.5 },
  premium: { monthly: 19.5, yearlyMonthEquiv: 15.5 },
  standardSibling: { monthly: 10, yearlyMonthEquiv: 8 },
  premiumSibling: { monthly: 16, yearlyMonthEquiv: 12.5 },
  /** Fixed bundle: 2 adults + 2 children (single price, not per child) */
  familyTwoAdultsTwoChildren: { monthly: 20, yearlyMonthEquiv: 16 },
  /** Per child: 1 adult, 2–8 children (Standard tier) */
  familyMultipleChildrenPerChild: { monthly: 10, yearlyMonthEquiv: 8 },
}

function mergePriceTier (base, patch) {
  if (!patch || typeof patch !== 'object') return base
  return {
    monthly: typeof patch.monthly === 'number' ? patch.monthly : base.monthly,
    yearlyMonthEquiv:
      typeof patch.yearlyMonthEquiv === 'number'
        ? patch.yearlyMonthEquiv
        : base.yearlyMonthEquiv
  }
}

export function mergeMembershipPrices (patch, defaults = DEFAULT_MEMBERSHIP_PRICES) {
  const out = { ...defaults }
  if (!patch || typeof patch !== 'object') return out
  for (const key of Object.keys(defaults)) {
    if (patch[key]) out[key] = mergePriceTier(defaults[key], patch[key])
  }
  return out
}

/**
 * Fetches portal membership prices; falls back to defaults if the endpoint is missing or errors.
 */
export async function fetchMembershipPrices (accessToken) {
  try {
    const { data } = await axios.get(
      config.serverURL +
        '/MembershipPricings?filter={"where":{"location":"' +
        config.branch.toLowerCase() +
        '"}}&access_token=' +
        accessToken
    )
    console.log("membershipPrices", data[0].prices)
    return mergeMembershipPrices(data[0].prices)
  } catch (e) {
    return DEFAULT_MEMBERSHIP_PRICES
  }
}

/**
 * @param {object} membership — plan from `memberships` (includes priceKey, familyPlan, …)
 * @param {1|2} memberType monthly = 1, yearly = 2
 * @param {number} familyChildCount
 * @param {typeof DEFAULT_MEMBERSHIP_PRICES} prices
 */
export function formatMembershipPayAmount (
  membership,
  memberType,
  familyChildCount,
  prices = DEFAULT_MEMBERSHIP_PRICES
) {
  const isMonthly = memberType === 1
  const pk = membership.priceKey
  const p = prices[pk]
  if (!p) return '—'

  if (membership.family && membership.familyPlan === 'multipleChildren') {
    const n = clampFamilyChildCountForPricing(familyChildCount, membership)
    if (isMonthly) {
      return (n * p.monthly).toFixed(2) + ' per month'
    }
    return (n * p.yearlyMonthEquiv * 12).toFixed(2) + ' per year'
  }

  if (membership.family && membership.familyPlan === 'twoAdultsTwoChildren') {
    if (isMonthly) {
      return p.monthly.toFixed(2) + ' per month'
    }
    return (p.yearlyMonthEquiv * 12).toFixed(2) + ' per year'
  }

  if (isMonthly) {
    return p.monthly.toFixed(2) + ' per month'
  }
  return (p.yearlyMonthEquiv * 12).toFixed(2) + ' per year'
}

function clampFamilyChildCountForPricing (count, membership) {
  const min = membership.minChildren || 2
  const max = membership.maxChildren || 8
  const n = parseInt(count, 10)
  if (Number.isNaN(n)) return min
  return Math.max(min, Math.min(n, max))
}

/** API stores type lowercased (`premium`); older records may still be `Premium`. */
function isPremiumType (type) {
  return String(type || '').toLowerCase() === 'premium'
}

/**
 * Billing total in GBP (major units), matching portal price tables — same basis as emails.
 * @param {object} membership — model from API
 * @param {typeof DEFAULT_MEMBERSHIP_PRICES} prices
 */
export function getMembershipBillingAmount (
  membership,
  prices = DEFAULT_MEMBERSHIP_PRICES
) {
  const isMonthly = membership.interval === 'monthly'
  let amount

  if (membership.isFamily) {
    const plan = membership.familyPlanType
    if (plan === 'twoAdultsTwoChildren') {
      const p = prices.familyTwoAdultsTwoChildren
      if (!p) return 0
      amount = isMonthly ? p.monthly : p.yearlyMonthEquiv * 12
    } else {
      const n = membership.familyChildCount || 2
      const usePremium = isPremiumType(membership.type)
      const per = usePremium
        ? prices.familyMultipleChildrenPerChildPremium ||
          prices.familyMultipleChildrenPerChild
        : prices.familyMultipleChildrenPerChild
      if (!per) return 0
      amount = isMonthly ? per.monthly * n : per.yearlyMonthEquiv * 12 * n
    }
  } else if (membership.isSibling) {
    const p = isPremiumType(membership.type)
      ? prices.premiumSibling
      : prices.standardSibling
    if (!p) return 0
    amount = isMonthly ? p.monthly : p.yearlyMonthEquiv * 12
  } else {
    const p = isPremiumType(membership.type) ? prices.premium : prices.standard
    if (!p) return 0
    amount = isMonthly ? p.monthly : p.yearlyMonthEquiv * 12
  }

  return amount
}

/**
 * Formatted £ string for confirmation emails after subscribe (matches prior behaviour).
 * @param {object} membership — model from API
 * @param {typeof DEFAULT_MEMBERSHIP_PRICES} prices
 */
export function formatEmailMembershipPrice (
  membership,
  prices = DEFAULT_MEMBERSHIP_PRICES
) {
  const isMonthly = membership.interval === 'monthly'
  const amount = getMembershipBillingAmount(membership, prices)

  if (isMonthly) {
    return '£' + amount + ' per month'
  }
  return '£' + amount + ' per year'
}
